import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Sizes } from 'enums/sizes';
import { useDialog } from 'hooks/useDialog';
import { LeadsArrivalDialogContent } from './LeadsArrivalDialogContent';
import { useUnreadLeadNotifications } from './useUnreadLeadNotifications';

/**
 * Page d'atterrissage après connexion : c'est le SEUL endroit où le popup s'ouvre.
 * Sans cette contrainte, « Ouvrir le dossier » (nouvel onglet) rejouait la modale
 * dans le nouvel onglet — sessionStorage est propre à chaque onglet, le garde-fou
 * de session n'y pouvait rien.
 */
const HOME_PATH = '/bord';

/**
 * Une seule ouverture par session de navigation : le popup marque l'ARRIVÉE dans
 * l'app, il ne doit pas resurgir à chaque retour sur le tableau de bord. Les leads
 * non accusés réception reviendront à la prochaine connexion (l'état vit dans
 * `isRead` côté back, ce drapeau ne sert qu'à ne pas être insistant).
 */
const SESSION_KEY = 'leads-arrival-dialog-shown';

/**
 * Accueil du courtier : si des leads partenaires lui ont été adressés depuis sa
 * dernière visite, une modale les présente avec leurs ajustements à faire. Monté
 * dans le layout du dashboard, sans rien afficher tant qu'il n'y a rien à dire.
 */
export const LeadsArrivalGate: React.FC = () => {
  const { openDialog } = useDialog();
  const { pathname } = useLocation();
  const isHome = pathname === HOME_PATH || pathname === '/';
  const alreadyShownThisSession = sessionStorage.getItem(SESSION_KEY) === '1';
  // Pas d'appel réseau hors tableau de bord : un onglet ouvert sur un dossier ne
  // doit rien interroger.
  const { leads, dismiss } = useUnreadLeadNotifications(isHome && !alreadyShownThisSession);
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    if (!isHome || hasOpenedRef.current || alreadyShownThisSession || leads.length === 0) {
      return;
    }
    hasOpenedRef.current = true;
    sessionStorage.setItem(SESSION_KEY, '1');

    openDialog(
      {
        title:
          leads.length === 1
            ? 'Un nouveau lead vous a été adressé'
            : `${leads.length} nouveaux leads vous ont été adressés`,
        size: Sizes['3xl']
      },
      <LeadsArrivalDialogContent leads={leads} onAcknowledged={dismiss} />
    );
  }, [isHome, leads, alreadyShownThisSession, openDialog, dismiss]);

  return null;
};

export default LeadsArrivalGate;
